import { City } from '../types/trip';
import { DuffelSearchResult } from '../types/duffel';

export interface AccommodationPreferences {
  type: 'hotels' | 'hostel' | 'villas';
  budget: 'low' | 'mid' | 'high';
}

interface SearchPayload {
  data: {
    rooms: number;
    location: {
      radius: number;
      geographic_coordinates: {
        longitude: number;
        latitude: number;
      };
    };
    check_in_date: string;
    check_out_date: string;
    guests: Array<{ type: string }>;
    filters: string[];
  };
}

export class AccommodationService {
  private static async fetchCsrfToken(): Promise<string | null> {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://travelapp-bens.onrender.com' 
        : 'http://localhost:5001';
      
      const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });
      const data = await response.json();
      return data.csrfToken;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return null;
    }
  }

  private static formatDateYYYYMMDD(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private static getAccommodationFilters(preferences: AccommodationPreferences, cityName?: string): string[] {
    const filters: string[] = [];
    
    // Special handling for problematic cities - use minimal filters
    const problematicCities = ['sydney', 'uluru', 'ayers rock'];
    const isProblematicCity = cityName && problematicCities.some(city => 
      cityName.toLowerCase().includes(city)
    );
    
    if (isProblematicCity) {
      console.log(`🔧 Using minimal filters for problematic city: ${cityName}`);
      // Only add basic accommodation type, no budget or quality filters
      switch (preferences.type) {
        case 'hostel':
          filters.push('hostel');
          break;
        case 'hotels':
          filters.push('hotel');
          break;
        case 'villas':
          filters.push('villa');
          break;
      }
      return filters;
    }
    
    // Add accommodation type filters for normal cities
    switch (preferences.type) {
      case 'hostel':
        // Use more general budget-related filters for hostels
        filters.push('budget_accommodation', 'shared_facilities', 'budget_friendly', 'hostel', 'backpacker');
        break;
      case 'hotels':
        filters.push('hotel', 'private_facilities');
        break;
      case 'villas':
        filters.push('villa', 'apartment', 'luxury', 'private_facilities');
        break;
    }
    
    // Add budget filters (removing under_100 as it may cause 422 errors)
    switch (preferences.budget) {
      case 'low':
        filters.push('budget_friendly');
        break;
      case 'mid':
        filters.push('mid_range', 'good_value');
        break;
      case 'high':
        filters.push('luxury', 'premium');
        break;
    }
    
    return filters;
  }

  private static sortAccommodationsByPreference(
    results: DuffelSearchResult[], 
    preferences: AccommodationPreferences
  ): DuffelSearchResult[] {

    
    // Step 1: Filter by accommodation type first
    const typeFiltered = this.filterByAccommodationType(results, preferences.type);

    
    if (typeFiltered.length === 0) {
      
      
      // Smart fallback based on original preference
      if (preferences.type === 'hostel') {

        
        // Try to find any budget accommodations regardless of type
        const sortedByPrice = this.sortByPrice(results);
        if (sortedByPrice.length > 0) {

          return this.selectByBudget(sortedByPrice, 'low'); // Use the cheapest accommodations
        }
        
        // Last resort: try budget hotels
        const budgetHotels = this.filterByAccommodationType(results, 'hotels');
        if (budgetHotels.length > 0) {

          return this.selectByBudget(budgetHotels, 'low'); // Force low budget for hotel fallback
        }
      } else if (preferences.type === 'hotels') {
        console.log(`🔄 No hotels found, trying hostels as budget alternative...`);
        const hostels = this.filterByAccommodationType(results, 'hostel');
        if (hostels.length > 0) {
          console.log(`✅ Found ${hostels.length} hostels as budget alternative`);
          return this.selectByBudget(hostels, preferences.budget);
        }
      }
      
      // Final fallback: use any available accommodation with budget preference
      console.log(`🔄 Final fallback: using any available accommodations with budget filtering`);
      if (results.length > 0) {
        return this.selectByBudget(results, preferences.budget);
      }
      return results;
    }
    
    // Step 2: Select best option within budget
    // For hostels, always prefer the cheapest options regardless of user's budget preference
    if (preferences.type === 'hostel') {
      
      return this.selectByBudget(typeFiltered, 'low');
    }
    
    return this.selectByBudget(typeFiltered, preferences.budget);
  }

  private static filterByAccommodationType(
    results: DuffelSearchResult[], 
    type: 'hotels' | 'hostel' | 'villas'
  ): DuffelSearchResult[] {
    let acceptedCount = 0;
    let rejectedCount = 0;
    
    const filtered = results.filter(accommodation => {
      const name = accommodation.accommodation?.name?.toLowerCase() || '';
      const description = (accommodation.accommodation as any)?.description?.toLowerCase() || '';
      
      switch (type) {
        case 'hostel':
          // FIRST: Accept clear hostel indicators (prioritize this)
          if (name.includes('hostel') || name.includes('backpack') || 
              name.includes('dorm') || description.includes('shared') ||
              name.includes('yha') || name.includes('youth') || 
              name.includes('bunk') || name.includes('pod')) {
            acceptedCount++;
            return true;
          }
          
          // SECOND: Accept budget accommodations as backup
          if (name.includes('budget') || name.includes('lodge') || 
              name.includes('motel') || name.includes('guesthouse') ||
              name.includes('backpacker') || name.includes('bed & breakfast') ||
              name.includes('b&b') || name.includes('homestay')) {
            acceptedCount++;
            return true;
          }
          
          // THIRD: Strict rejection of obvious luxury hotels (but be less strict)
          if (name.includes('resort') || name.includes('marriott') || 
              name.includes('hilton') || name.includes('hyatt') || 
              name.includes('sheraton') || name.includes('luxury') || 
              name.includes('premium') || name.includes('pullman') ||
              name.includes('ritz') || name.includes('westin')) {
            rejectedCount++;
            return false;
          }
          
          // FOURTH: Be more lenient with remaining accommodations for hostels (price will filter further)
          // Only reject if it's clearly a high-end establishment
          if (name.includes('grand') || name.includes('royal') || 
              name.includes('imperial') || name.includes('palace')) {
            rejectedCount++;
            return false;
          }
          
          // FIFTH: Default accept for hostels (let price filtering handle the rest)
          acceptedCount++;
          return true;
        
        case 'hotels':
          const isHotel = name.includes('hotel') || name.includes('resort') || 
                         name.includes('inn') || name.includes('suites');
          if (isHotel) acceptedCount++; else rejectedCount++;
          return isHotel;
        
        case 'villas':
          const isVilla = name.includes('villa') || name.includes('apartment') || 
                         name.includes('suite') || name.includes('residence') ||
                         name.includes('condo');
          if (isVilla) acceptedCount++; else rejectedCount++;
          return isVilla;
        
        default:
          acceptedCount++;
          return true;
      }
    });
    
    console.log(`🏨 Type filter (${type}): ✅ ${acceptedCount} accepted, ❌ ${rejectedCount} rejected (${filtered.length}/${results.length} total passed)`);
    return filtered;
  }

  private static selectByBudget(
    results: DuffelSearchResult[], 
    budget: 'low' | 'mid' | 'high'
  ): DuffelSearchResult[] {
    if (results.length === 0) return results;
    
    // Calculate price percentiles for this specific set of accommodations
    const prices = results
      .map(r => parseFloat(r.cheapest_rate_total_amount || '0'))
      .filter(p => p > 0)
      .sort((a, b) => a - b);
    
    if (prices.length === 0) return results;
    
    const p33 = prices[Math.floor(prices.length * 0.33)] || prices[0];
    const p67 = prices[Math.floor(prices.length * 0.67)] || prices[prices.length - 1];
    
    console.log(`💰 Price range for this accommodation type: Low: £0-${p33}, Mid: £${p33}-${p67}, High: £${p67}+`);
    
    // Filter by budget bracket
    const budgetFiltered = results.filter(accommodation => {
      const price = parseFloat(accommodation.cheapest_rate_total_amount || '0');
      
      switch (budget) {
        case 'low':
          return price <= p33;
        case 'mid':
          return price > p33 && price <= p67;
        case 'high':
          return price > p67;
        default:
          return true;
      }
    });
    
    if (budgetFiltered.length === 0) {
      console.log(`⚠️ No accommodations in ${budget} budget range, using all available options`);
      // For places with limited options, don't be too strict about budget
      return this.sortByPrice(results);
    }
    
    console.log(`💰 After budget filter (${budget}): ${budgetFiltered.length} accommodations`);
    
    // Sort by rating (best first), then by price (cheapest first)
    return budgetFiltered.sort((a, b) => {
      const ratingA = a.accommodation?.rating || 0;
      const ratingB = b.accommodation?.rating || 0;
      
      // Primary sort: rating (higher is better)
      if (ratingA !== ratingB) {
        return ratingB - ratingA;
      }
      
      // Secondary sort: price (lower is better)
      const priceA = parseFloat(a.cheapest_rate_total_amount || '0');
      const priceB = parseFloat(b.cheapest_rate_total_amount || '0');
      return priceA - priceB;
    });
  }

  private static sortByPrice(results: DuffelSearchResult[]): DuffelSearchResult[] {
    return results.sort((a, b) => {
      const priceA = parseFloat(a.cheapest_rate_total_amount || '0');
      const priceB = parseFloat(b.cheapest_rate_total_amount || '0');
      return priceA - priceB; // Cheapest first
    });
  }

  public static async searchAccommodationsForCity(
    city: City,
    checkInDate: Date,
    checkOutDate: Date,
    preferences: AccommodationPreferences,
    guests: number = 2
  ): Promise<DuffelSearchResult | null> {
    try {
      const csrfToken = await this.fetchCsrfToken();
      if (!csrfToken) {
        throw new Error('Failed to fetch CSRF token');
      }

      const filters = this.getAccommodationFilters(preferences, city.name);
      
      // Special handling for Krabi - use corrected coordinates if needed
      let coordinates = {
        longitude: city.coordinates.lng,
        latitude: city.coordinates.lat,
      };
      
      let searchRadius = 10; // Default radius
      
      if (city.name.toLowerCase().includes('krabi')) {
        console.log('🔧 KRABI DETECTED in service - Using corrected coordinates');
        coordinates = {
          longitude: 98.9063,  // Krabi Town center
          latitude: 8.0863
        };
        console.log('🔧 Service - Original coords:', city.coordinates, '-> New coords:', coordinates);
      }
      
      // Special handling for Sydney - use smaller radius to avoid API issues
      if (city.name.toLowerCase().includes('sydney')) {
        console.log('🔧 SYDNEY DETECTED - Using smaller radius to avoid API issues');
        searchRadius = 5; // Smaller radius for Sydney
      }
      
      const searchPayload: SearchPayload = {
        data: {
          rooms: 1,
          location: {
            radius: searchRadius,
            geographic_coordinates: coordinates,
          },
          check_in_date: this.formatDateYYYYMMDD(checkInDate),
          check_out_date: this.formatDateYYYYMMDD(checkOutDate),
          guests: Array(guests).fill({ type: 'adult' }),
          filters
        },
      };

      console.log(`🏨 Searching accommodations for ${city.name} with preferences:`, {
        type: preferences.type,
        budget: preferences.budget,
        filters,
        coordinates: `${coordinates.latitude}, ${coordinates.longitude}`,
        originalCoordinates: `${city.coordinates.lat}, ${city.coordinates.lng}`,
        dates: `${this.formatDateYYYYMMDD(checkInDate)} to ${this.formatDateYYYYMMDD(checkOutDate)}`
      });

      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://travelapp-bens.onrender.com' 
        : 'http://localhost:5001';
      
      const response = await fetch(`${API_BASE_URL}/api/duffel/accommodation/auto-select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(searchPayload),
      });

      let data;
      if (!response.ok) {
        // Fallback to regular search if auto-select endpoint doesn't exist
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/duffel/test-search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'CSRF-Token': csrfToken,
          },
          credentials: 'include',
          body: JSON.stringify(searchPayload),
        });

        if (!fallbackResponse.ok) {
          throw new Error(`Search failed with status ${fallbackResponse.status}`);
        }

        data = await fallbackResponse.json();
      } else {
        data = await response.json();
      }
      
      console.log(`📊 API returned ${data.data?.results?.length || 0} accommodations for ${city.name}`);
      
      if (data.data?.results?.length > 0) {
        const allResults = data.data.results;
        const sortedResults = this.sortAccommodationsByPreference(allResults, preferences);
        
        let selectedAccommodation;
        
        if (sortedResults.length > 0) {
          selectedAccommodation = sortedResults[0];
          console.log(`✅ Auto-selected accommodation for ${city.name}:`, 
            selectedAccommodation?.accommodation?.name, 
            `(${preferences.type}, ${preferences.budget} budget)`);
        } else {
          console.log(`⚠️ No accommodations passed filtering for ${city.name}`);
          console.log(`🔄 Falling back to first available accommodation for ${city.name}`);
          selectedAccommodation = allResults[0];
        }
        
        // Enhanced: Attach ALL results to the selected accommodation for AI Assist to use
        if (selectedAccommodation) {
          // Store all original results from the API
          (selectedAccommodation as any).__allResults = allResults;
          // Also store the sorted/filtered results if different from all results
          if (sortedResults.length > 0 && sortedResults.length !== allResults.length) {
            (selectedAccommodation as any).__filteredResults = sortedResults;
          }
          console.log(`🎯 Attached ${allResults.length} total results to selected accommodation for ${city.name}`);
        }
        
        return selectedAccommodation;
      }

      console.log(`⚠️ No accommodations found for ${city.name} with current preferences and coordinates ${coordinates.latitude}, ${coordinates.longitude}`);
      
      // Try with larger radius if initial search failed
      console.log(`🔄 Trying larger search radius for ${city.name}...`);
      const expandedSearchPayload = {
        ...searchPayload,
        data: {
          ...searchPayload.data,
          location: {
            ...searchPayload.data.location,
            radius: 25 // Much larger radius
          }
        }
      };
      
      try {
        const expandedResponse = await fetch(`${API_BASE_URL}/api/duffel/test-search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'CSRF-Token': csrfToken,
          },
          credentials: 'include',
          body: JSON.stringify(expandedSearchPayload),
        });
        
        if (expandedResponse.ok) {
          const expandedData = await expandedResponse.json();
          if (expandedData.data?.results?.length > 0) {
            console.log(`✅ Found ${expandedData.data.results.length} accommodations with expanded radius for ${city.name}`);
            const expandedAllResults = expandedData.data.results;
            const sortedResults = this.sortAccommodationsByPreference(expandedAllResults, preferences);
            const selectedAccommodation = sortedResults.length > 0 ? sortedResults[0] : expandedAllResults[0];
            
            // Attach all results to the selected accommodation
            if (selectedAccommodation) {
              (selectedAccommodation as any).__allResults = expandedAllResults;
              if (sortedResults.length > 0 && sortedResults.length !== expandedAllResults.length) {
                (selectedAccommodation as any).__filteredResults = sortedResults;
              }
              console.log(`🎯 Attached ${expandedAllResults.length} expanded results to selected accommodation for ${city.name}`);
            }
            
            return selectedAccommodation;
          }
        }
      } catch (expandedError) {
        console.log(`❌ Expanded search also failed for ${city.name}:`, expandedError);
      }
      
      return null;

    } catch (error) {
      console.error(`❌ Error searching accommodations for ${city.name}:`, error);
      return null;
    }
  }

  public static extractPreferencesFromTripData(tripData: any): AccommodationPreferences {
    const aiAssistData = tripData?.tripDetails?.aiAssistData;
    
    // Default preferences
    let type: 'hotels' | 'hostel' | 'villas' = 'hotels';
    let budget: 'low' | 'mid' | 'high' = 'mid';
    
    // Extract accommodation type preference
    if (aiAssistData?.accommodation?.length > 0) {
      const accommodationType = aiAssistData.accommodation[0].toLowerCase();
      if (accommodationType.includes('hostel')) {
        type = 'hostel';
      } else if (accommodationType.includes('villa') || accommodationType.includes('apartment')) {
        type = 'villas';
      } else {
        type = 'hotels';
      }
    }
    
    // Extract budget preference
    if (aiAssistData?.budget) {
      const budgetLevel = aiAssistData.budget.toLowerCase();
      if (budgetLevel.includes('low') || budgetLevel.includes('budget') || budgetLevel.includes('cheap')) {
        budget = 'low';
      } else if (budgetLevel.includes('high') || budgetLevel.includes('luxury') || budgetLevel.includes('premium')) {
        budget = 'high';
      } else {
        budget = 'mid';
      }
    }
    
    return { type, budget };
  }
} 